import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useGuardedNavigate } from "../../hooks/useGuardedNavigate";
import {
  confirmDiscardFormEdits,
  markFormSaved,
  useFormEditingGuard,
} from "../../hooks/useFormEditingGuard";
import {
  Layout,
  theme,
  Tabs,
  Row,
  Col,
  Card,
  Button,
  message,
  Form,
  Input,
  Select,
  Modal,
  Space,
  Switch,
  Upload,
  Divider,
} from "antd";
import {
  GoldOutlined,
  EditOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import type { TabsProps, UploadProps } from "antd";
import { SelectProps } from "antd/lib/select";
import axios from "axios";
import LoadingOverlay from "react-loading-overlay-ts";

import { ProductTable } from "../../components/Table";
import AddonTable from "../../components/Table/AddonTable";
import AdminSideBar from "../../components/SideBar/AdminSidebar";
import AdminTopBar from "../../components/TopBar/AdminTopbar";
import GroupCommunicationTab from "../../components/GroupCommunication/GroupCommunicationTab";
import CommunicationSetupAlert from "../../components/GroupCommunication/CommunicationSetupAlert";
import {
  getCommunicationSetupIssues,
  COMMUNICATION_SECTIONS,
} from "../../components/GroupCommunication/communicationSetupChecks";
import {
  getGroupTabState,
  setGroupTabState,
} from "../../utils/groupTabStorage";

import { editGroupApi, getGroupApi } from "../../services/Group/GroupApis";
import {
  addProductApi,
  editProductApi,
  removeProductApi,
} from "../../services/Product/ProductApis";
import { addAddonApi, editAddonApi } from "../../services/Addon/AddonApis";
import {
  removeDocumentApi,
  getDocumentsApi,
  generateBulkDocumentApi,
} from "../../services/Document/DocumentApis";

const { Content } = Layout;

interface IGroupProps {
  group: string;
}

type TextAreaRefs = {
  [key: string]: React.RefObject<any>;
};

const bank: SelectProps["options"] = [
  {
    label: "ABSA",
    value: "ABSA",
  },
  {
    label: "Access",
    value: "Access",
  },
  {
    label: "African Bank",
    value: "African Bank",
  },
  {
    label: "Bidvest Bank",
    value: "Bidvest Bank",
  },
  {
    label: "Capitec",
    value: "Capitec",
  },
  {
    label: "FinBond",
    value: "FinBond",
  },
  {
    label: "FNB",
    value: "FNB",
  },
  {
    label: "Nedbank",
    value: "Nedbank",
  },
  {
    label: "Standard Bank",
    value: "Standard Bank",
  },
  {
    label: "TymeBank",
    value: "TymeBank",
  },
];

const nutunDebitTypes: SelectProps["options"] = [
  {
    label: "Fixed",
    value: "fixed",
  },
  {
    label: "Variable",
    value: "variable",
  },
];

const AdminGroupManagementPage: React.FC<IGroupProps> = () => {
  const navigate = useGuardedNavigate();
  const [form] = Form.useForm(); // This will create a form instance
  const { groupId }: any = useParams();
  const [formDirty, setFormDirty] = useState(false);

  const [group, setGroup] = useState<any>({});
  const [products, setProducts] = useState<any>([]);
  const [addons, setAddons] = useState<any>([]);
  const [documents, setDocuments] = useState<any>([]);
  const [communicationDocuments, setCommunicationDocuments] = useState<any>([]);
  const [formData, setFormData] = useState<any>();
  const [formDisabled, setFormDisabled] = useState<any>({
    docs: true,
    debiCheck: true,
    sms: true,
    sftp: true,
    basic: true,
  });
  const { markSaved } = useFormEditingGuard({
    id: `admin-group-management-${groupId}`,
    formDisabled,
    formDirty,
  });
  const [isActive, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [generateDocuments, setGenerateDocuments] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string>("");
  const [key, setKey] = useState<string>("");
  const [customVariable, setCustomVariable] = useState("PolicyHolder");
  const [selectedTextArea, setSelectedTextArea] = useState<string>("");
  const [imageUrl, setImageUrl] = useState("");
  const [activeGroupTab, setActiveGroupTab] = useState(() => {
    const stored = getGroupTabState(groupId, true);
    return stored?.activeGroupTab || "group-info";
  });
  const [communicationSubTab, setCommunicationSubTab] = useState(() => {
    const stored = getGroupTabState(groupId, true);
    return (
      stored?.communicationSubTab || COMMUNICATION_SECTIONS.documentTemplates
    );
  });

  const textAreaRefs: TextAreaRefs = {
    NewPolicySms: useRef<any>(null),
    DocumentSms: useRef<any>(null),
    NonPaymentSms: useRef<any>(null),
    SmsLapse: useRef<any>(null),
  };

  const document: SelectProps["options"] = documents.map((doc: any) => {
    return {
      value: doc.id,
      label: doc.Name,
      key: doc.id,
    };
  });

  const emailTemplateOptions: SelectProps["options"] = communicationDocuments.map(
    (doc: any) => ({
      value: doc.id,
      label: doc.Name,
      key: doc.id,
    })
  );

  const props: UploadProps = {
    name: "file",
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const base64 = await convertFileToBase64(file);
        const accessToken = localStorage.getItem("AccessToken");
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/v1/document/image`,
          {
            base64: base64,
          },
          {
            headers: {
              AccessToken: accessToken ? accessToken : "",
            },
          }
        );
        if (onSuccess) {
          setImageUrl(response.data.data);
          onSuccess(response.data);
        }
      } catch (error) {
        if (onError) {
          onError(new Error("Upload failed"));
        }
      }
    },
    onChange(info) {
      if (info.file.status !== "uploading") {
        console.log(info.file, info.fileList);
      }
      if (info.file.status === "done") {
        message.success(`${info.file.name} file uploaded successfully`);
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
      setFormData({ name: info.file.name });
    },
  };

  const onChange = (name: string, checked: boolean) => {
    setFormData((prevFormData: any) => ({
      ...prevFormData,
      [name]: checked,
    }));
  };

  const onChangeDocument = async (e: any) => {
    const { data } = await getDocumentsApi(group.id);

    const docType = data.filter((doc: any) => {
      return doc.id === e;
    });

    {
      form.setFieldsValue({
        DocumentType: docType[0].Type,
      });
    }
  };

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const handleGenerateDocuments = (data: any) => {
    setGenerateDocuments(true);
    setLoading(true);

    generateBulkDocumentApi(data.Document, "Document", groupId)
      .then((res) => {
        message.success("Successfully generated documents.");
        setGenerateDocuments(false);
        setLoading(false);
        return res;
      })
      .catch((err) => {
        setGenerateDocuments(false);
        setLoading(false);
        console.log(err);
      });
  };

  const handleAddProduct = async (data: any) => {
    setActive(true);

    return await addProductApi(data)
      .then((res) => {
        setProducts([...products, res.data]);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleAddAddon = async (data: any) => {
    setActive(true);

    return await addAddonApi(data)
      .then((res) => {
        setAddons([...addons, res.data]);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleAddVariable = () => {
    if (selectedTextArea === "") {
      message.error("Please select a TextArea first.");
      return;
    }

    const textAreaRef = textAreaRefs[selectedTextArea];
    const variableId = `{{${customVariable}}}`;

    if (textAreaRef?.current) {
      const textArea = textAreaRef.current.resizableTextArea.textArea;
      const currentValue = textArea.value;
      const selectionStart = textArea.selectionStart;
      const selectionEnd = textArea.selectionEnd;

      // Insert the variable into the current value
      const newValue =
        currentValue.substring(0, selectionStart) +
        variableId +
        currentValue.substring(selectionEnd);

      // Update the value of the TextArea through the form instance
      form.setFieldsValue({ [selectedTextArea]: newValue });
    }
  };

  const handleAddDetails = async (id: any, data: any, num: number) => {
    setActive(true);
    setLoading(true);

    // Convert NupayMinAmount to number if it exists
    const processedData = {
      ...data,
      CompanyLogo: imageUrl,
      ...(data.NupayMinAmount && { NupayMinAmount: Number(data.NupayMinAmount) })
    };

    return await editGroupApi(id, processedData)
      .then((res) => {
        if (num === 1) {
          setFormDisabled({
            docs: !formDisabled.docs,
            debiCheck: formDisabled.debiCheck,
            sms: formDisabled.sms,
            sftp: formDisabled.sftp,
            basic: formDisabled.basic,
          });
        } else if (num === 2) {
          setFormDisabled({
            docs: formDisabled.docs,
            debiCheck: !formDisabled.debiCheck,
            sms: formDisabled.sms,
            sftp: formDisabled.sftp,
            basic: formDisabled.basic,
          });
        } else if (num === 3) {
          setFormDisabled({
            docs: formDisabled.docs,
            debiCheck: formDisabled.debiCheck,
            sms: !formDisabled.sms,
            sftp: formDisabled.sftp,
            basic: formDisabled.basic,
          });
        } else if (num === 4) {
          setFormDisabled({
            docs: formDisabled.docs,
            debiCheck: formDisabled.debiCheck,
            sms: formDisabled.sms,
            sftp: !formDisabled.sftp,
            basic: formDisabled.basic,
          });
        } else if (num === 5) {
          setFormDisabled({
            docs: formDisabled.docs,
            debiCheck: formDisabled.debiCheck,
            sms: formDisabled.sms,
            sftp: formDisabled.sftp,
            basic: !formDisabled.basic,
          });
        }

        setActive(false);
        setLoading(false);
        markFormSaved(markSaved, setFormDirty);
        return res;
      })
      .catch((err) => {
        setActive(false);
        setLoading(false);
        console.log(err);
      });
  };

  const handleUpdateProduct = async (
    id: string,
    planId: any,
    categoryId: any,
    data: any
  ) => {
    setActive(true);

    return await editProductApi(id, planId, categoryId, data)
      .then((res) => {
        const uppdatedProducts: any = products.map((user: any) => {
          if (Number(id) === Number(user.id)) {
            return { ...user, ...res.data };
          }

          return user;
        });
        setActive(false);
        setProducts(uppdatedProducts);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleUpdateAddon = async (id: string, data: any) => {
    setActive(true);

    return await editAddonApi(id, data)
      .then((res) => {
        const updatedAddons: any = addons.map((addon: any) => {
          if (id === addon.id) {
            return { ...addon, ...res.data };
          }

          return addon;
        });
        setActive(false);
        setAddons(updatedAddons);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const toggleFormEdit = async (num: number) => {
    const sectionKeys: Record<number, string> = {
      1: "docs",
      2: "debiCheck",
      3: "sms",
      4: "sftp",
      5: "basic",
    };
    const sectionKey = sectionKeys[num];

    if (
      !(await confirmDiscardFormEdits(formDisabled, formDirty, sectionKey))
    ) {
      return;
    }

    if (num === 1) {
      setFormDisabled({
        docs: !formDisabled.docs,
        debiCheck: formDisabled.debiCheck,
        sms: formDisabled.sms,
        sftp: formDisabled.sftp,
        basic: formDisabled.basic,
      });
    } else if (num === 2) {
      setFormDisabled({
        docs: formDisabled.docs,
        debiCheck: !formDisabled.debiCheck,
        sms: formDisabled.sms,
        sftp: formDisabled.sftp,
        basic: formDisabled.basic,
      });
    } else if (num === 3) {
      setFormDisabled({
        docs: formDisabled.docs,
        debiCheck: formDisabled.debiCheck,
        sms: !formDisabled.sms,
        sftp: formDisabled.sftp,
        basic: formDisabled.basic,
      });
    } else if (num === 4) {
      setFormDisabled({
        docs: formDisabled.docs,
        debiCheck: formDisabled.debiCheck,
        sms: formDisabled.sms,
        sftp: !formDisabled.sftp,
        basic: formDisabled.basic,
      });
    } else if (num === 5) {
      setFormDisabled({
        docs: formDisabled.docs,
        debiCheck: formDisabled.debiCheck,
        sms: formDisabled.sms,
        sftp: formDisabled.sftp,
        basic: !formDisabled.basic,
      });
    }
  };

  const convertFileToBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        resolve(event?.target?.result);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDeleteProduct = (id: string, key: string) => {
    setKey(key);
    setConfirmDelete(true);
    setProductToDelete(id);
  };

  const handleDeleteDocument = (id: string) => {
    setActive(true);

    removeDocumentApi(id)
      .then((res) => {
        const deleted = res.data;
        setDocuments(
          documents.filter((document: any) => document.id !== deleted.id)
        );
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  const handleBack = () => {
    void navigate("/admin/groups");
  };

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 14 },
  };

  const tailLayout = {
    labelCol: { span: 10 },
    wrapperCol: { offset: 6, span: 14 },
  };

  const setupIssues = getCommunicationSetupIssues({
    group,
    documents,
    communicationDocuments,
    formData,
  });

  const navigateToCommunicationSection = (section: string) => {
    setActiveGroupTab("communication");
    setCommunicationSubTab(section);
  };

  const items: TabsProps["items"] = [
    {
      key: "group-info",
      label: `Group Info`,
      children: (
        <div className="content-border">
          <CommunicationSetupAlert
            issues={setupIssues}
            onNavigate={navigateToCommunicationSection}
          />
          <Row style={{ paddingTop: "24px" }} gutter={[24, 24]}>
            <Col span={12}>
              <Card
                title="Basic Info"
                style={{ backgroundColor: "	#DCDCDC" }}
                bordered={true}
                hoverable={true}
              >
                <Form {...layout}>
                  <div className="card-content">
                    <h4>Group Name:</h4>
                    <span> {group.GroupName}</span>
                  </div>
                  <div className="card-content">
                    <h4>Cellphone:</h4>
                    <span> {group.Cellphone}</span>
                  </div>
                  <div className="card-content">
                    <h4>Telephone:</h4>
                    <span> {group.TelNumber}</span>
                  </div>
                  <div className="card-content">
                    <h4>Email:</h4>
                    <span> {group.Email}</span>
                  </div>
                  <div className="card-content">
                    <h4>Fax Number:</h4>
                    <span> {!group.FaxNumber ? "N/A" : group.FaxNumber}</span>
                  </div>
                  <div className="card-content">
                    <h4>FSP Number:</h4>
                    <span> {!group.FSPNumber ? "N/A" : group.FSPNumber}</span>
                  </div>
                </Form>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                title="Address"
                style={{ backgroundColor: "	#DCDCDC" }}
                bordered={true}
                hoverable={true}
              >
                <div className="card-content">
                  <h4>Address Line 1:</h4>
                  <span> {group.AddressLine1}</span>
                </div>
                <div className="card-content">
                  <h4>Address Line 2:</h4>
                  <span> {group.AddressLine2}</span>
                </div>
                <div className="card-content">
                  <h4>Suburb:</h4>
                  <span> {group.Suburb}</span>
                </div>
                <div className="card-content">
                  <h4>City/Town:</h4>
                  <span> {group.CityTown}</span>
                </div>
                <div className="card-content">
                  <h4>Province:</h4>
                  <span> {group.Province}</span>
                </div>
                <div className="card-content">
                  <h4>Postal Code:</h4>
                  <span> {group.PostalCode}</span>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      key: "products",
      label: `Products`,
      children: (
        <div className="content-border">
          <ProductTable
            products={products}
            groupId={groupId}
            handleAdd={handleAddProduct}
            handleUpdate={handleUpdateProduct}
            handleDelete={handleDeleteProduct}
          />
        </div>
      ),
    },
    {
      key: "addons",
      label: `Addons`,
      children: (
        <div className="content-border">
          <AddonTable
            addons={addons}
            groupId={groupId}
            handleAdd={handleAddAddon}
            handleUpdate={handleUpdateAddon}
            handleDelete={handleDeleteProduct}
          />
        </div>
      ),
    },
    {
      key: "communication",
      label: "Communication",
      children: (
        <GroupCommunicationTab
          mode="admin"
          groupId={groupId}
          group={group}
          documents={documents}
          communicationDocuments={communicationDocuments}
          form={form}
          formData={formData}
          formDisabled={formDisabled}
          loading={loading}
          layout={layout}
          tailLayout={tailLayout}
          documentOptions={document}
          textAreaRefs={textAreaRefs}
          communicationSubTab={communicationSubTab}
          setCommunicationSubTab={(key) => {
            setCommunicationSubTab(key);
            setGroupTabState(
              groupId,
              { activeGroupTab: "communication", communicationSubTab: key },
              true
            );
          }}
          setupIssues={setupIssues}
          onNavigateToSection={navigateToCommunicationSection}
          onDeleteDocument={handleDeleteDocument}
          onSmsConfigSubmit={(values) => handleAddDetails(groupId, values, 1)}
          onSmsCredentialsSubmit={(values) =>
            handleAddDetails(groupId, values, 3)
          }
          onEmailSettingsSubmit={(values) =>
            handleAddDetails(groupId, values, 5)
          }
          onSendBulkSms={() => undefined}
          onToggleSmsConfigEdit={() => toggleFormEdit(1)}
          onToggleSmsCredentialsEdit={() => toggleFormEdit(3)}
          onToggleEmailSettingsEdit={() => toggleFormEdit(5)}
          onAddVariable={handleAddVariable}
          onSelectTextArea={setSelectedTextArea}
          onSmsEnabledChange={(checked) => onChange("SmsEnabled", checked)}
          onEmailNotificationChange={(checked) =>
            onChange("EmailNotification", checked)
          }
          onChangeDocument={onChangeDocument}
          onFormDirty={() => setFormDirty(true)}
          onGenerateDocuments={() => setGenerateDocuments(true)}
          emailTemplateOptions={emailTemplateOptions}
        />
      ),
    },
    ...(group.GroupLevel === 1
      ? [
          {
      key: "integration",
      label: `Integration Info`,
      children: (
        <div className="content-border">
          <Row style={{ paddingTop: "24px" }} gutter={[24, 24]}>
            <Col span={12}>
              <Card
                title="Debicheck Info"
                bordered={true}
                hoverable={true}
                extra={
                  <EditOutlined
                    onClick={() => toggleFormEdit(2)}
                    style={{ color: "blue" }}
                  />
                }
              >
                <Form
                  {...layout}
                  onFinish={(values) => handleAddDetails(groupId, values, 2)}
                  initialValues={formData}
                  disabled={formDisabled.debiCheck}
                  form={form}
                  onValuesChange={() => setFormDirty(true)}
                >
                  {/* <div className="card-content">
                    <Form.Item name="NutunUsername" label="Nutun Username">
                      <Input size="large" placeholder="Username" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="NutunPassword" label="Nutun Password">
                      <Input size="large" placeholder="Password" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="NutunUserCode" label="Nutun Usercode">
                      <Input size="large" placeholder="Usercode" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="NutunLogoCode" label="Nutun Logocode">
                      <Input size="large" placeholder="Logocode" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="NutunShortname" label="Nutun Shortname">
                      <Input size="large" placeholder="Shortname" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="NutunUniqueID" label="Nutun UniqueID">
                      <Input size="large" placeholder="UniqueID" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item
                      name="NutunDebitValueType"
                      label="Nutun Debit Type"
                    >
                      <Select
                        id="NutunDebitValueType"
                        options={nutunDebitTypes}
                        size="large"
                        style={{ width: "100%" }}
                        placeholder="Nutun Debit Type"
                      />
                    </Form.Item>
                  </div> */}

                  <Divider />

                  <div className="card-content">
                    <Form.Item name="NupayUsername" label="Nupay Username">
                      <Input size="large" placeholder="Username" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="NupayPassword" label="Nupay Password">
                      <Input.Password size="large" placeholder="Password" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item
                      name="NupayMerchantNumber"
                      label="Nupay Merchant Number"
                    >
                      <Input size="large" placeholder="Merchant Number" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="NupayMinAmount" label="Nupay Min Amount">
                      <Input size="large" placeholder="Min Amount" type="number" />
                    </Form.Item>
                  </div>

                  <Divider />

                  <div className="card-content">
                    <Form.Item
                      name="PayAtPrefix"
                      label="Pay@ Prefix"
                      rules={[
                        {
                          pattern: /^.{5}$/,
                          message: "Pay@ prefix must be 5 characters",
                        },
                      ]}
                    >
                      <Input size="large" placeholder="Pay@ Prefix" />
                    </Form.Item>
                  </div>

                  {formDisabled.debiCheck === false && (
                    <div className="card-content">
                      <Form.Item {...tailLayout}>
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Button
                            style={{ marginBottom: 10 }}
                            block
                            size="large"
                            htmlType="submit"
                            loading={loading}
                          >
                            Submit
                          </Button>
                        </Space>
                      </Form.Item>
                    </div>
                  )}
                </Form>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                title="SFTP Info"
                bordered={true}
                hoverable={true}
                extra={
                  <EditOutlined
                    onClick={() => toggleFormEdit(4)}
                    style={{ color: "blue" }}
                  />
                }
              >
                <Form
                  {...layout}
                  onFinish={(values) => handleAddDetails(groupId, values, 4)}
                  initialValues={formData}
                  disabled={formDisabled.sftp}
                  form={form}
                  onValuesChange={() => setFormDirty(true)}
                >
                  <div className="card-content">
                    <Form.Item
                      name="SftpUsername"
                      label="SFTP Username"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Input size="large" placeholder="SFTP Username" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item
                      name="SftpPassword"
                      label="Sftp Password"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Input.Password size="large" placeholder="Sftp Password" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item
                      name="SftpURL"
                      label="Sftp URL"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Input size="large" placeholder="Sftp ID Directory" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item
                      name="SftpIDDirectory"
                      label="Sftp ID Directory"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Input size="large" placeholder="Sftp ID Directory" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item
                      name="SftpApplicationDirectory"
                      label="Sftp Application Directory"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="Sftp Application Directory"
                      />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item
                      name="SftpPolicyDirectory"
                      label="Sftp Policy Directory"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Input size="large" placeholder="Sftp Policy Directory" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item
                      name="SftpDataDirectory"
                      label="Sftp Data Directory"
                      rules={[
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Input size="large" placeholder="Sftp Data Directory" />
                    </Form.Item>
                  </div>
                  {formDisabled.sftp === false && (
                    <div className="card-content">
                      <Form.Item {...tailLayout}>
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Button
                            style={{ marginBottom: 10 }}
                            block
                            size="large"
                            htmlType="submit"
                          >
                            Submit
                          </Button>
                        </Space>
                      </Form.Item>
                    </div>
                  )}
                </Form>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
        ]
      : []),
    {
      key: "basic-info",
      label: `Basic Info`,
      children: (
        <div className="content-border">
          <Row style={{ paddingTop: "24px" }} gutter={[24, 24]}>
            <Col span={12}>
              <Card
                title="Basic Info"
                bordered={true}
                hoverable={true}
                extra={
                  <EditOutlined
                    onClick={() => toggleFormEdit(5)}
                    style={{ color: "blue" }}
                  />
                }
              >
                <Form
                  {...layout}
                  onFinish={(values) => handleAddDetails(groupId, values, 5)}
                  initialValues={formData}
                  disabled={formDisabled.basic}
                  form={form}
                  onValuesChange={() => setFormDirty(true)}
                >
                  <div className="card-content">
                    <Form.Item
                      name="PolicyPrefix"
                      label="Policy Prefix"
                      rules={[
                        {
                          pattern: /^.{2,3}$/,
                          message: "Policy prefix must be 2 or 3 characters",
                        },
                      ]}
                    >
                      <Input
                        maxLength={3}
                        size="large"
                        placeholder="Policy Prefix"
                      />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="AccountNumber" label="Account Number">
                      <Input size="large" placeholder="Account Number" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="AccountType" label="Account Type">
                      <Input size="large" placeholder="Account Type" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="Bank" label="Bank">
                      <Select
                        id="Bank"
                        options={bank}
                        size="large"
                        style={{ width: "100%" }}
                        placeholder="Bank"
                      />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="BranchCode" label="Branch Code">
                      <Input size="large" placeholder="Branch Code" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="FuneralEnabled" label="Funeral Enabled">
                      <Switch
                        defaultChecked={formData?.FuneralEnabled}
                        onChange={(checked) =>
                          onChange("FuneralEnabled", checked)
                        }
                      />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="VATEnabled" label="VAT Enabled">
                      <Switch
                        defaultChecked={formData?.VATEnabled}
                        onChange={(checked) => onChange("VATEnabled", checked)}
                      />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item
                      name="CanRunOwnLapses"
                      label="Can run own lapses"
                      extra="When off, overdue members bubble up to the nearest parent that can run its own lapses."
                    >
                      <Switch
                        defaultChecked={formData?.CanRunOwnLapses}
                        onChange={(checked) =>
                          onChange("CanRunOwnLapses", checked)
                        }
                      />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item
                      name="VATNumber"
                      label="VAT Number"
                      rules={[
                        {
                          required: formData?.VATEnabled,
                        },
                      ]}
                    >
                      <Input size="large" placeholder="VAT Number" />
                    </Form.Item>
                  </div>
                  <div className="card-content">
                    <Form.Item name="CompanyLogo" label="Company Logo">
                      <Upload
                        listType="picture"
                        defaultFileList={[
                          {
                            uid: "-1",
                            name: "Company_Logo",
                            status: "done",
                            url: formData?.CompanyLogo,
                            thumbUrl: formData?.CompanyLogo,
                          },
                        ]}
                        {...props}
                      >
                        <Button icon={<UploadOutlined />}>
                          Click to Upload
                        </Button>
                      </Upload>
                    </Form.Item>
                  </div>

                  {formDisabled.basic === false && (
                    <div className="card-content">
                      <Form.Item {...tailLayout}>
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Button
                            style={{ marginBottom: 10 }}
                            block
                            size="large"
                            htmlType="submit"
                            loading={loading}
                          >
                            Submit
                          </Button>
                        </Space>
                      </Form.Item>
                    </div>
                  )}
                </Form>
              </Card>
            </Col>
          </Row>
        </div>
      ),
    },
  ];

  const onCancelGenerate = () => {
    setGenerateDocuments(false);
  };

  const onCancelDelete = () => {
    setConfirmDelete(false);
    setProductToDelete("");
  };

  const onConfirmDelete = () => {
    setConfirmDelete(false);
    setActive(true);

    removeProductApi(productToDelete)
      .then((res) => {
        const deleted = res.data;
        setProducts(
          products.filter((product: any) => product.id !== deleted.id)
        );
        setAddons(addons.filter((addon: any) => addon.id !== deleted.id));
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  useEffect(() => {
    setActive(true);
    getGroupApi(String(groupId))
      .then((res) => {
        setCommunicationDocuments(res.data.CommunicationDocument);
        setDocuments(res.data.Document);
        setProducts(res.data.Product);
        setAddons(res.data.Addon);
        setFormData(res.data);
        setGroup(res.data);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setActive(false);
      });
  }, []);

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <AdminSideBar />
        <Layout className="site-layout">
          <AdminTopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <h2>
                <GoldOutlined
                  style={{
                    paddingRight: "5px",
                    color: "#f04f05",
                  }}
                />
                {group.GroupName}
              </h2>
              <div className="interactions">
                <Button onClick={handleBack} size="large">
                  <ArrowLeftOutlined style={{ paddingRight: "5px" }} />
                  Back
                </Button>
              </div>
              <Tabs
                type="card"
                activeKey={activeGroupTab}
                onChange={(key) => {
                  setActiveGroupTab(key);
                  setGroupTabState(
                    groupId,
                    { activeGroupTab: key, communicationSubTab },
                    true
                  );
                }}
                items={items}
              />
            </div>
          </Content>
        </Layout>
      </Layout>

      {/* Confirmation Modal */}
      {key === "1" ? (
        <Modal
          title="Confirm Remove"
          open={confirmDelete}
          onOk={onConfirmDelete}
          onCancel={onCancelDelete}
        >
          Are you sure you want to move this Product inactive?
        </Modal>
      ) : (
        <Modal
          title="Confirm Revert"
          open={confirmDelete}
          onOk={onConfirmDelete}
          onCancel={onCancelDelete}
        >
          Are you sure you want to move this Product active?
        </Modal>
      )}

      {/* Generate Bulk Documents Modal */}

      <Modal
        title="Generate Bulk Documents"
        open={generateDocuments}
        onCancel={onCancelGenerate}
        footer={false}
      >
        <Form
          {...layout}
          onFinish={(values) => handleGenerateDocuments(values)}
        >
          <Form.Item name="Document" label="Document">
            <Select
              onChange={(e: any) => onChangeDocument(e)}
              id="Document"
              options={document}
              size="large"
              style={{ width: "100%" }}
              placeholder="Document"
            />
          </Form.Item>

          <Form.Item name="DocumentType" label="Document Type">
            <Select
              disabled={true}
              id="DocumentType"
              size="large"
              style={{ width: "100%" }}
              placeholder="Document Type"
            />
          </Form.Item>

          <Form.Item {...tailLayout}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                htmlType="submit"
                loading={loading}
                style={{ marginBottom: 10 }}
                block
                size="large"
              >
                Submit
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </LoadingOverlay>
  );
};

export default AdminGroupManagementPage;
